import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Shield, Eye, EyeOff, MapPin, Key } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SuperAdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [adminKey, setAdminKey] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = (fr, en) => language === 'fr' ? fr : en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/api/super-admin/auth/login`, {
        email, password, admin_key: adminKey
      });
      localStorage.setItem('super-admin-token', res.data.token);
      localStorage.setItem('super-admin-user', JSON.stringify(res.data));
      navigate('/super-admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || t('Connexion échouée', 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'linear-gradient(145deg, #0a0a0f 0%, #1a0a1e 40%, #0d0d15 100%)' }}
      data-testid="super-admin-login-page">

      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 bg-gradient-to-br from-purple-600 to-indigo-700 shadow-lg shadow-purple-500/30">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">
            Super Admin
          </h1>
          <p className="text-white/40 text-sm mt-1">LocalSite.Club</p>
        </div>

        {/* Login Card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-1">
            <Key className="w-5 h-5 text-purple-400" />
            <h2 className="font-bold text-white text-base">
              {t('Accès Super Admin', 'Super Admin Access')}
            </h2>
          </div>
          <p className="text-white/50 text-xs mb-5">
            {t('Connexion réservée aux administrateurs globaux', 'Reserved for global administrators')}
          </p>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm mb-4" data-testid="sa-login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-white/60 mb-1.5">{t('Courriel', 'Email')}</label>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)} required
                className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-white placeholder-white/30 text-sm focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 outline-none transition-all"
                placeholder={t('admin@localsite.club', 'admin@localsite.club')}
                data-testid="sa-login-email"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-white/60 mb-1.5">{t('Mot de passe', 'Password')}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                  className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-white placeholder-white/30 text-sm focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 outline-none transition-all pr-12"
                  placeholder="••••••••"
                  data-testid="sa-login-password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-white/60 mb-1.5">{t('Clé secrète Admin', 'Secret Admin Key')}</label>
              <input
                type="password" value={adminKey} onChange={e => setAdminKey(e.target.value)} required
                className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-white placeholder-white/30 text-sm focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 outline-none transition-all"
                placeholder={t('Clé d\'accès...', 'Access key...')}
                data-testid="sa-login-key"
              />
            </div>

            <button type="submit" disabled={loading}
              className="w-full py-3 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 transition-all disabled:opacity-50 shadow-lg shadow-purple-500/20"
              data-testid="sa-login-submit">
              {loading ? t('Connexion...', 'Signing in...') : t('Accéder', 'Access')}
            </button>
          </form>
        </div>

        <p className="text-center text-white/20 text-[10px] mt-6">
          {t('Accès réservé — Toute tentative non autorisée est journalisée', 'Restricted access — All unauthorized attempts are logged')}
        </p>
      </div>
    </div>
  );
};

export default SuperAdminLoginPage;
